/*
 * <store-selector> — VIS-211.
 *
 * Turns the navbar "Trova il tuo negozio di fiducia" entries into the entry
 * point for picking a favorite store without leaving the page:
 *
 *  - Any [data-store-selector-trigger] in the document (the desktop top-bar
 *    link + the drawer footer's store row) opens a dialog. The triggers are
 *    <a href> with a no-JS fallback, so we preventDefault only when we can
 *    actually open the dialog.
 *  - The store-locator section is lazy-loaded into the dialog on first open via
 *    the Section Rendering API (fetched once, then reused). Its custom elements
 *    upgrade because the host loads their modules; we tag the injected
 *    <store-locator> with data-hydration-base-url so multi-page hydration fetches
 *    from the locator URL, not the host page.
 *  - This element is a read-only consumer of the favorite (VIS-210's
 *    store-favorites.js): on load and on `store-favorite:changed` it reflects the
 *    favorite into the dialog title/intro/summary and the desktop top-bar label.
 *    VIS-979: the summary's remove button is its one write, clearing the favorite.
 *  - VIS-565: for first-time visitors with no favorite it shows a one-time
 *    coachmark anchored to the visible trigger, nudging them to pick a store.
 */

import { getFavorite, clearFavorite, favoriteStoreUrl } from '@theme/store-favorites';

// Shown-once flag for the VIS-565 favorite-store nudge.
const NUDGE_SEEN_KEY = 'vo:storeNudgeSeen';

class StoreSelector extends HTMLElement {
  connectedCallback() {
    this.locatorUrl = this.dataset.locatorUrl || '/pages/trova-negozio';
    this.locatorSection = this.dataset.locatorSection || 'store-locator';

    this.dialogComponent = this.querySelector('dialog-component');
    this.panel = this.querySelector('[data-store-selector-panel]');
    this.body = this.querySelector('[data-store-selector-body]');
    this.loading = this.querySelector('[data-store-selector-loading]');
    this.titleEl = this.querySelector('[data-store-selector-title]');
    this.introEl = this.querySelector('[data-store-selector-intro]');
    this.favoriteSlot = this.querySelector('[data-store-selector-favorite]');
    this.favoriteName = this.querySelector('[data-store-selector-favorite-name]');
    this.favoriteRemove = this.querySelector('[data-store-selector-favorite-remove]');

    this.nudge = this.querySelector('[data-store-nudge]');
    this.nudgeClose = this.querySelector('[data-store-nudge-close]');
    this.nudgeTrigger = null;

    this.injected = false;
    this.injecting = false;

    document.addEventListener('click', this.#onTriggerClick);
    window.addEventListener('store-favorite:changed', this.#reflectFavorite);
    // VIS-916: a bfcache restore re-enters the page without re-running
    // connectedCallback and without replaying `store-favorite:changed`, so a
    // favorite picked on the locator page never reached this document.
    window.addEventListener('pageshow', this.#onPageShow);
    if (this.dialogComponent) this.dialogComponent.addEventListener('dialog:open', this.#onDialogOpen);
    if (this.favoriteRemove) this.favoriteRemove.addEventListener('click', this.#onRemoveFavorite);

    this.#reflectFavorite();
    this.#maybeShowNudge();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onTriggerClick);
    window.removeEventListener('store-favorite:changed', this.#reflectFavorite);
    window.removeEventListener('pageshow', this.#onPageShow);
    if (this.dialogComponent) this.dialogComponent.removeEventListener('dialog:open', this.#onDialogOpen);
    if (this.favoriteRemove) this.favoriteRemove.removeEventListener('click', this.#onRemoveFavorite);
    this.#teardownNudgeListeners();
  }

  // VIS-979: clearing hides the card (and this button) through
  // #reflectFavorite, so park focus on the panel instead of letting it drop to
  // <body> behind the open dialog.
  #onRemoveFavorite = () => {
    clearFavorite();
    if (this.panel) this.panel.focus();
  };

  // The dialog auto-focuses the first focusable element (the close button) on
  // open, which paints its focus ring as a box. Move focus to the panel so the
  // X stays a bare icon; keyboard users can still Tab to it.
  #onDialogOpen = () => {
    if (this.panel) this.panel.focus();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#reflectFavorite();
  };

  #onTriggerClick = (event) => {
    const trigger = event.target.closest('[data-store-selector-trigger]');
    if (!trigger) return;
    // Engaging with any trigger retires the nudge (even the no-JS nav path).
    this.#dismissNudge();
    // Only hijack the click if we can open the dialog; otherwise let the
    // anchor's href navigate to the locator page (no-JS / not-upgraded path).
    if (!this.dialogComponent || typeof this.dialogComponent.showDialog !== 'function') return;
    event.preventDefault();
    this.dialogComponent.showDialog();
    void this.#ensureLocator(trigger);
  };

  async #ensureLocator(trigger) {
    if (this.injected || this.injecting || !this.body) return;
    this.injecting = true;
    if (this.loading) this.loading.hidden = false;

    try {
      const url = `${this.locatorUrl}?section_id=${encodeURIComponent(this.locatorSection)}`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`store-locator section responded ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const locator = doc.querySelector('store-locator');
      if (!locator) throw new Error('no <store-locator> in the section response');

      locator.dataset.hydrationBaseUrl = this.locatorUrl;
      if (this.loading) this.loading.hidden = true;
      // importNode so the element upgrades in this document; appending the
      // foreign node directly would not run its connectedCallback reliably.
      this.body.appendChild(document.importNode(locator, true));
      this.injected = true;
    } catch (error) {
      console.warn('[store-selector] could not load the locator; falling back to navigation.', error);
      if (this.loading) this.loading.hidden = true;
      const href = trigger && trigger.getAttribute('href');
      if (href && href !== '#') window.location.href = href;
    } finally {
      this.injecting = false;
    }
  }

  #reflectFavorite = () => {
    const favorite = getFavorite();
    const hasFavorite = favorite != null;

    if (this.titleEl) {
      const next = hasFavorite ? this.titleEl.dataset.labelSelected : this.titleEl.dataset.labelSelect;
      if (next) this.titleEl.textContent = next;
    }

    // The intro copy belongs to the "select" state only; once a favorite is set
    // the dialog leads straight into the favorite summary (Figma 2434:28835).
    if (this.introEl) this.introEl.hidden = hasFavorite;

    if (this.favoriteSlot) this.favoriteSlot.hidden = !hasFavorite;
    if (hasFavorite && this.favoriteName) {
      this.favoriteName.textContent = favorite.name;
      this.favoriteName.href = favoriteStoreUrl(favorite);
    }

    // Desktop top-bar label → favorite name (default text preserved as fallback).
    document.querySelectorAll('[data-store-favorite-name]').forEach((el) => {
      if (el.dataset.defaultText === undefined) el.dataset.defaultText = el.textContent.trim();
      el.textContent = hasFavorite && favorite.name ? favorite.name : el.dataset.defaultText;
    });

    // A chosen favorite means the nudge has done its job, so retire it.
    if (hasFavorite) this.#dismissNudge();
  };

  // --- VIS-565 favorite-store nudge -----------------------------------------

  #maybeShowNudge() {
    if (!this.nudge) return;
    // First-time visitors with no favorite only, shown once, and never on the
    // locator page itself (choosing a store is already the point there).
    if (getFavorite() != null || this.#nudgeSeen() || this.#onLocatorPage()) return;

    const trigger = this.#visibleTrigger();
    if (!trigger) return;

    this.nudgeTrigger = trigger;
    trigger.classList.add('store-nudge-pulse');
    this.nudge.hidden = false;

    if (this.nudgeClose) this.nudgeClose.addEventListener('click', this.#onNudgeClose);
    window.addEventListener('resize', this.#onNudgeResize);
    window.addEventListener('scroll', this.#positionNudge, { passive: true });
    document.addEventListener('keydown', this.#onNudgeKeydown);

    // Position once it has dimensions, then fade in on the next frame.
    requestAnimationFrame(() => {
      this.#positionNudge();
      requestAnimationFrame(() => {
        if (this.nudge) this.nudge.classList.add('is-visible');
      });
    });
  }

  // The visible trigger depends on the breakpoint: the desktop top-bar link is
  // hidden ≤749px, and the drawer footer's store row keeps layout while the
  // drawer is closed but sits translated off-screen (VIS-1073), so having
  // layout is not enough. Pick the first trigger actually on screen, else fall
  // back below. [data-store-nudge-skip] opts a trigger out: the PDP contact
  // CTA is a trigger only in the no-favorite state (VIS-805), which is the
  // only state the nudge shows in, so without the skip it would win on phones
  // where the header triggers are off screen.
  #visibleTrigger() {
    const triggers = [...document.querySelectorAll('[data-store-selector-trigger]:not([data-store-nudge-skip])')];
    const onScreen = triggers.find((trigger) => this.#onScreen(trigger));
    if (onScreen) return onScreen;

    // Below 750px nothing that opens the store selector is on screen: the
    // top-bar link is display:none, the drawer footer's store row sits inside a
    // closed drawer, and VIS-707 gave the header store-finder icon's slot to the
    // wishlist heart. Anchor the nudge to the menu button instead, which is what
    // now leads to the store row, rather than dropping it on phones entirely.
    const menuButton = document.querySelector('.header__icon--menu');
    if (menuButton instanceof HTMLElement && this.#onScreen(menuButton)) return menuButton;

    // Desktop page opened scrolled down (scroll restoration, back) with a
    // header that is not sticky: the top-bar link sits above the viewport and
    // the menu button is hidden. Anchor to it anyway; the scroll listener
    // brings the bubble along once the visitor scrolls back up.
    return triggers.find((trigger) => this.#isRendered(trigger)) ?? null;
  }

  // A zero-size rect covers display:none; the bounds check covers elements
  // with layout that are translated or scrolled out of the viewport. The
  // closed drawer is also visibility:hidden (inherited), which matters for its
  // "Prenota appuntamento" CTA: a trigger in the no-favorite state, it lands
  // inside the viewport under the header once the drawer is slid up.
  #onScreen(element) {
    if (!this.#isRendered(element)) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.right > 0 &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.top < window.innerHeight
    );
  }

  #isRendered(element) {
    if (getComputedStyle(element).visibility !== 'visible') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // Crossing the 750px breakpoint (rotating a phone, resizing a window) can
  // hide the trigger picked on load, so pick again before repositioning.
  #onNudgeResize = () => {
    const trigger = this.#visibleTrigger();
    if (trigger && trigger !== this.nudgeTrigger) {
      if (this.nudgeTrigger) this.nudgeTrigger.classList.remove('store-nudge-pulse');
      trigger.classList.add('store-nudge-pulse');
      this.nudgeTrigger = trigger;
    }
    this.#positionNudge();
  };

  #positionNudge = () => {
    if (!this.nudge || !this.nudgeTrigger || this.nudge.hidden) return;

    const margin = 8;
    const rect = this.nudgeTrigger.getBoundingClientRect();
    const width = this.nudge.offsetWidth;
    const center = rect.left + rect.width / 2;
    const maxLeft = window.innerWidth - width - margin;
    const left = Math.max(margin, Math.min(center - width / 2, maxLeft));

    this.nudge.style.top = `${rect.bottom + margin}px`;
    this.nudge.style.left = `${left}px`;
    // Keep the arrow pointing at the trigger even when the bubble is clamped.
    const arrowX = Math.max(16, Math.min(center - left, width - 16));
    this.nudge.style.setProperty('--store-nudge-arrow-x', `${arrowX}px`);
  };

  #onNudgeClose = () => this.#dismissNudge();

  #onNudgeKeydown = (event) => {
    if (event.key === 'Escape') this.#dismissNudge();
  };

  #dismissNudge = () => {
    if (!this.nudge || this.nudge.hidden) return;
    this.#markNudgeSeen();
    this.nudge.classList.remove('is-visible');
    this.nudge.hidden = true;
    if (this.nudgeTrigger) this.nudgeTrigger.classList.remove('store-nudge-pulse');
    this.nudgeTrigger = null;
    this.#teardownNudgeListeners();
  };

  #teardownNudgeListeners() {
    if (this.nudgeClose) this.nudgeClose.removeEventListener('click', this.#onNudgeClose);
    window.removeEventListener('resize', this.#onNudgeResize);
    window.removeEventListener('scroll', this.#positionNudge);
    document.removeEventListener('keydown', this.#onNudgeKeydown);
  }

  #onLocatorPage() {
    return window.location.pathname.startsWith(this.locatorUrl);
  }

  #nudgeSeen() {
    try {
      return localStorage.getItem(NUDGE_SEEN_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markNudgeSeen() {
    try {
      localStorage.setItem(NUDGE_SEEN_KEY, '1');
    } catch {
      // Private mode / quota — the nudge simply reappears next session.
    }
  }

}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector);
}
